import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	Alert,
	Box,
	CircularProgress,
	Snackbar,
	Tab,
	Tabs,
	Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useStyles from "./styles";
import AbaExibicao from "../../../../components/centralDeAlertas/config/abaExibicao";
import AbaAutomacoes from "../../../../components/centralDeAlertas/config/abaAutomacoes";
import AbaInteligentes from "../../../../components/centralDeAlertas/config/abaInteligentes";
import AbaParametros from "../../../../components/centralDeAlertas/config/abaParametros";
import {
	getVisibilidade,
	getAutomacoes,
	getClientes,
	getTodosHosts,
	getParametrosCentral,
	getCategoriasInteligentes,
	salvarCategoriaInteligente,
	salvarTipoAlerta,
	salvarExcecao,
	removerExcecao as apiRemoverExcecao,
	salvarAutomacao,
	removerAutomacao as apiRemoverAutomacao,
	salvarParametrosCentral,
	testarAutomacao,
} from "../../../../components/centralDeAlertas/config/api";

// Cada aba tem o próprio path (/config/:aba) para ser linkável e sobreviver a
// refresh/voltar do navegador. A ordem segue a ordem visual das <Tab/>.
const ABAS = ["exibicao", "automacoes", "inteligentes", "parametros"];
const PATH_CONFIG = "/interno/centralDeAlertas/config";

export default function CentralDeAlertasConfig() {
	const theme = useTheme();
	const styles = useStyles(theme);

	const { aba: abaPath } = useParams();
	const navigate = useNavigate();
	// Slug desconhecido cai na primeira aba enquanto o redirect abaixo corrige a URL.
	const aba = Math.max(0, ABAS.indexOf(abaPath));

	useEffect(() => {
		if (!ABAS.includes(abaPath)) {
			navigate(`${PATH_CONFIG}/${ABAS[0]}`, { replace: true });
		}
	}, [abaPath, navigate]);
	const [carregando, setCarregando] = useState(true);
	const [tipos, setTipos] = useState([]);
	const [excecoes, setExcecoes] = useState([]);
	const [automacoes, setAutomacoes] = useState([]);
	const [clientes, setClientes] = useState([]);
	const [allHosts, setAllHosts] = useState([]);
	const [parametros, setParametros] = useState(null);
	const [categoriasInteligentes, setCategoriasInteligentes] = useState(null);
	const [snackbar, setSnackbar] = useState({
		aberto: false,
		mensagem: "",
		severity: "success",
	});

	const avisar = useCallback(
		(mensagem, severity = "success") =>
			setSnackbar({ aberto: true, mensagem, severity }),
		[]
	);

	const recarregarExibicao = useCallback(async () => {
		const { tipos, excecoes } = await getVisibilidade();
		setTipos(tipos || []);
		setExcecoes(excecoes || []);
	}, []);

	const recarregarAutomacoes = useCallback(async () => {
		setAutomacoes((await getAutomacoes()) || []);
	}, []);

	useEffect(() => {
		let ativo = true;
		(async () => {
			setCarregando(true);
			const [vis, autos, cli, hosts, params, inteligentes] =
				await Promise.allSettled([
					getVisibilidade(),
					getAutomacoes(),
					getClientes(),
					getTodosHosts(),
					getParametrosCentral(),
					getCategoriasInteligentes(),
				]);
			if (!ativo) return;
			if (vis.status === "fulfilled") {
				setTipos(vis.value?.tipos || []);
				setExcecoes(vis.value?.excecoes || []);
			}
			if (autos.status === "fulfilled") setAutomacoes(autos.value || []);
			if (cli.status === "fulfilled") setClientes(cli.value || []);
			if (hosts.status === "fulfilled") setAllHosts(hosts.value || []);
			if (params.status === "fulfilled") setParametros(params.value || null);
			if (inteligentes.status === "fulfilled") {
				setCategoriasInteligentes(inteligentes.value || null);
			}
			if (
				[vis, autos, cli, hosts, params, inteligentes].some(
					(r) => r.status === "rejected"
				)
			) {
				avisar("Falha ao carregar parte dos dados da tela.", "warning");
			}
			setCarregando(false);
		})();
		return () => {
			ativo = false;
		};
	}, [avisar]);

	// --- Handlers de exibição ---
	// Devolve true/false: o modal de regras só fecha quando o backend aceitou
	// (regex inválida volta 400 e o operador precisa continuar no formulário).
	const handleSalvarTipo = async (tipo) => {
		try {
			await salvarTipoAlerta(tipo);
			await recarregarExibicao();
			avisar("Tipo de alerta atualizado.");
			return true;
		} catch (e) {
			avisar(mensagemErro(e), "error");
			return false;
		}
	};

	const handleSalvarExcecao = async (excecao) => {
		try {
			await salvarExcecao(excecao);
			await recarregarExibicao();
			avisar("Exceção salva.");
			return true;
		} catch (e) {
			avisar(mensagemErro(e), "error");
			return false;
		}
	};

	const handleRemoverExcecao = async (id) => {
		try {
			await apiRemoverExcecao(id);
			await recarregarExibicao();
			avisar("Exceção removida.");
		} catch (e) {
			avisar(mensagemErro(e), "error");
		}
	};

	// --- Handlers de automações ---
	const handleSalvarAutomacao = async (payload) => {
		try {
			await salvarAutomacao(payload);
			await recarregarAutomacoes();
			avisar("Automação salva.");
			return true;
		} catch (e) {
			avisar(mensagemErro(e), "error");
			return false;
		}
	};

	const handleRemoverAutomacao = async (id) => {
		try {
			await apiRemoverAutomacao(id);
			await recarregarAutomacoes();
			avisar("Automação removida.");
		} catch (e) {
			avisar(mensagemErro(e), "error");
		}
	};

	const handleTestar = (payload) => testarAutomacao(payload);

	// --- Handlers de categorias inteligentes ---
	const handleSalvarCategoriaInteligente = async (payload) => {
		try {
			// O backend devolve o estado inteiro atualizado (mesmo shape do GET).
			const atualizadas = await salvarCategoriaInteligente(payload);
			setCategoriasInteligentes(atualizadas);
			avisar("Detecção atualizada. O painel passa a usar a nova regra.");
			return true;
		} catch (e) {
			avisar(mensagemErro(e), "error");
			return false;
		}
	};

	// --- Handlers de parâmetros ---
	const handleSalvarParametros = async (payload) => {
		try {
			const atualizados = await salvarParametrosCentral(payload);
			setParametros(atualizados);
			avisar("Parâmetros salvos. O painel passa a usar a nova janela.");
			return true;
		} catch (e) {
			avisar(mensagemErro(e), "error");
			return false;
		}
	};

	return (
		<Box sx={styles.container}>
			<Box sx={styles.header}>
				<Typography sx={styles.titulo}>Configuração de Alertas</Typography>
				<Typography sx={styles.subtitulo}>
					Quais tipos de alerta aparecem na Central, automações por tempo e
					parâmetros de correlação
				</Typography>
			</Box>

			<Tabs
				value={aba}
				onChange={(_, v) => navigate(`${PATH_CONFIG}/${ABAS[v]}`)}
				sx={styles.abas}
			>
				<Tab label="Exibição" />
				<Tab label="Automações" />
				<Tab label="Inteligentes" />
				<Tab label="Parâmetros" />
			</Tabs>

			{carregando ? (
				<Box sx={styles.loading}>
					<CircularProgress />
				</Box>
			) : aba === 0 ? (
				<AbaExibicao
					tipos={tipos}
					excecoes={excecoes}
					clientes={clientes}
					allHosts={allHosts}
					onSalvarTipo={handleSalvarTipo}
					onSalvarExcecao={handleSalvarExcecao}
					onRemoverExcecao={handleRemoverExcecao}
					carregando={carregando}
				/>
			) : aba === 1 ? (
				<AbaAutomacoes
					automacoes={automacoes}
					tipos={tipos}
					clientes={clientes}
					allHosts={allHosts}
					onSalvar={handleSalvarAutomacao}
					onRemover={handleRemoverAutomacao}
					onTestar={handleTestar}
					carregando={carregando}
				/>
			) : aba === 2 ? (
				<AbaInteligentes
					config={categoriasInteligentes}
					onSalvar={handleSalvarCategoriaInteligente}
				/>
			) : (
				<AbaParametros
					parametros={parametros}
					onSalvar={handleSalvarParametros}
				/>
			)}

			<Snackbar
				open={snackbar.aberto}
				autoHideDuration={4000}
				onClose={() => setSnackbar((s) => ({ ...s, aberto: false }))}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={() => setSnackbar((s) => ({ ...s, aberto: false }))}
					severity={snackbar.severity}
					variant="filled"
				>
					{snackbar.mensagem}
				</Alert>
			</Snackbar>
		</Box>
	);
}

function mensagemErro(e) {
	return (
		e?.response?.data?.message ||
		e?.response?.data?.error ||
		e?.message ||
		"Erro inesperado"
	);
}
